import React, { useState, useEffect, useRef } from 'react';
import { StarRating } from './StarRating';
import { formatDistanceToNow, format } from 'date-fns';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Edit2, Trash2, Check, X, Mic, MicOff, Play, Pause, Volume2, CalendarIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Label } from './ui/label';
import { cn } from '@/lib/utils';

interface ReadRowContentProps {
  rating?: number;
  reviewText?: string;
  finishedAt?: string;
  startedAt?: string;
  readAt?: string;
  bookId?: string;
  isOwnProfile?: boolean;
  onReviewUpdate?: (bookId: string, newReviewText: string, newRating?: number, startedAt?: Date | null, finishedAt?: Date | null) => Promise<void>;
  onBookRemove?: (bookId: string) => Promise<void>;
}

function relativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return 'some time ago';
  }
}

export function ReadRowContent({
  rating,
  reviewText,
  finishedAt,
  startedAt,
  readAt,
  bookId,
  isOwnProfile = false,
  onReviewUpdate,
  onBookRemove
}: ReadRowContentProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(reviewText || '');
  const [editRating, setEditRating] = useState(rating || 0);
  const [editStartedAt, setEditStartedAt] = useState<Date | undefined>(startedAt ? new Date(startedAt) : undefined);
  const [editFinishedAt, setEditFinishedAt] = useState<Date | undefined>(finishedAt ? new Date(finishedAt) : undefined);
  const [isLoading, setIsLoading] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('casual');
  const [recordingDuration, setRecordingDuration] = useState(0);
  
  // Voice recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioUrlRef = useRef<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const completedDate = finishedAt || readAt;
  const maxRecordingSeconds = 120; // 2 minutes max

  // Update editText, editRating, and dates when props change
  useEffect(() => {
    setEditText(reviewText || '');
    setEditRating(rating || 0);
    setEditStartedAt(startedAt ? new Date(startedAt) : undefined);
    setEditFinishedAt(finishedAt ? new Date(finishedAt) : undefined);
  }, [reviewText, rating, startedAt, finishedAt]);

  // Cleanup audio resources when component unmounts or editing stops
  useEffect(() => {
    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current = null;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const handleSaveEdit = async () => {
    if (!bookId || !onReviewUpdate) return;
    
    setIsLoading(true);
    try {
      await onReviewUpdate(
        bookId, 
        editText, 
        editRating, 
        editStartedAt || null, 
        editFinishedAt || null
      );
      setIsEditing(false);
      // Reset voice recording state
      resetVoiceRecording();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(reviewText || '');
    setEditRating(rating || 0);
    setEditStartedAt(startedAt ? new Date(startedAt) : undefined);
    setEditFinishedAt(finishedAt ? new Date(finishedAt) : undefined);
    setIsEditing(false);
    // Reset voice recording state
    resetVoiceRecording();
  };

  const resetVoiceRecording = () => {
    setIsRecording(false);
    setHasRecording(false);
    setIsPlaying(false);
    setIsProcessing(false);
    setRecordingDuration(0);
    audioChunksRef.current = [];
    
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleRemove = async () => {
    if (!bookId || !onBookRemove) return;
    
    if (confirm('Are you sure you want to remove this book from your profile? This will delete all associated data including rating and review.')) {
      setIsLoading(true);
      try {
        await onBookRemove(bookId);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 44100,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setRecordingDuration(0);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        audioUrlRef.current = audioUrl;
        setHasRecording(true);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          if (newDuration >= maxRecordingSeconds) {
            stopRecording();
            toast({
              title: "Recording stopped",
              description: `Maximum recording duration (${maxRecordingSeconds}s) reached.`,
            });
          }
          return newDuration;
        });
      }, 1000);

      mediaRecorder.start(100);
      setIsRecording(true);
      
      toast({
        title: "Recording started",
        description: `Speak your review thoughts (max ${maxRecordingSeconds}s)...`,
      });
    } catch (err) {
      console.error('Error starting recording:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: 'Could not access microphone. Please check permissions.',
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      toast({
        title: "Recording stopped",
        description: "You can now play back or process your recording.",
      });
    }
  };

  const playRecording = () => {
    if (audioUrlRef.current) {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current = null;
      }

      const audio = new Audio(audioUrlRef.current);
      audioElementRef.current = audio;
      
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => {
        setIsPlaying(false);
        toast({
          variant: "destructive",
          title: "Error",
          description: 'Error playing audio',
        });
      };
      
      audio.play();
    }
  };

  const stopPlaying = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const processRecording = async () => {
    if (!hasRecording || !audioUrlRef.current) return;

    setIsProcessing(true);
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication error - please sign in again');
      }

      const getStyleInstructions = (style: string) => {
        switch (style) {
          case 'casual':
            return "write like a smart friend. conversational with natural contractions, short punchy sentences, no jargon, no emojis, no spoilers.";
          case 'formal':
            return "polished and neutral. structured paragraphs, precise vocabulary, no slang or emoji, balanced tone, no spoilers.";
          case 'warm-personal':
            return "first-person and reflective. include one or two feelings or moments that resonated, gentle cadence, generous but honest, no spoilers.";
          case 'hot-take':
            return "open with a bold one-sentence verdict. tight and energetic, include 3–5 crisp bullet justifications, end with a memorable closer, no spoilers.";
          default:
            return "write like a smart friend. conversational with natural contractions, short punchy sentences, no jargon, no emojis, no spoilers.";
        }
      };

      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice-review.webm');
      formData.append('language', 'en');
      formData.append('prompt', `Transform this voice note into a natural, conversational book review that sounds like a real reader sharing their genuine thoughts. Write only the review content itself - no titles, headings, or lead-ins like "Review of..." or "Here are my thoughts...". Remove filler words and organize thoughts naturally while maintaining the authentic voice and opinions. Style instructions: ${getStyleInstructions(selectedStyle)}`);
      // Align with edge function API: send structured mode instead of only freeform prompt
      const modeMap = { 'casual': 'casual', 'formal': 'formal', 'warm-personal': 'warm_personal', 'hot-take': 'hot_take' } as const;
      const mode = modeMap[selectedStyle as keyof typeof modeMap] ?? 'casual';
      formData.append('mode', mode);

      const response = await fetch(`https://zjubnjpiajoscjmogyqb.supabase.co/functions/v1/reviews-transcribe-and-summarize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Edge function error:', data);
        
        if (data.error?.stage) {
          const { stage, status, body } = data.error;
          const shortBody = body.length > 120 ? body.slice(0, 120) + '...' : body;
          throw new Error(`Voice service error — ${stage} ${status}: ${shortBody}`);
        }
        
        throw new Error(`Server error: ${response.status}`);
      }

      const reviewText = data.tidyReview || data.summary || data.transcript;
      if (reviewText) {
        // Replace the current text with the AI-generated review (or transcript fallback)
        setEditText(reviewText);
        toast({
          title: "Review generated!",
          description: data.mode === 'transcript' ? "Your voice note has been transcribed." : "Your voice recording has been transformed into a written review.",
        });
      } else {
        throw new Error('No review text received from AI processing');
      }

    } catch (error) {
      console.error('Error processing recording:', error);
      toast({
        variant: "destructive",
        title: "Processing failed",
        description: error instanceof Error ? error.message : 'Failed to process voice recording. Please try again.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Rating and date row */}
      <div className="flex items-center gap-2 flex-wrap">
        {typeof rating === 'number' && rating > 0 && (
          <>
            <StarRating 
              value={rating} 
              readonly 
              size="sm"
              className="flex-shrink-0"
            />
            <span className="text-xs text-muted-foreground">
              ({rating.toFixed(1)})
            </span>
          </>
        )}
        
        {completedDate && (
          <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
            Read {relativeTime(completedDate)}
          </span>
        )}
      </div>
      
      {/* Review */}
      {isEditing ? (
        <div className="space-y-3">
          {/* Editable rating */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Rating
            </label>
            <div className="flex items-center gap-2">
              <StarRating 
                value={editRating} 
                readonly={false}
                size="sm"
                className="flex-shrink-0"
                onChange={setEditRating}
              />
              <span className="text-xs text-muted-foreground">
                ({editRating > 0 ? editRating.toFixed(1) : 'No rating'})
              </span>
            </div>
          </div>

          {/* Date Started */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date Started</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !editStartedAt && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {editStartedAt ? format(editStartedAt, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={editStartedAt}
                  onSelect={setEditStartedAt}
                  disabled={(date) => date > new Date()}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Finished */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Date Finished</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !editFinishedAt && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {editFinishedAt ? format(editFinishedAt, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={editFinishedAt}
                  onSelect={setEditFinishedAt}
                  disabled={(date) => 
                    date > new Date() || 
                    (editStartedAt ? date < editStartedAt : false)
                  }
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Editable review text with voice recording */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Review
              </label>
              {/* Voice recording controls */}
              <div className="flex items-center gap-2">
                <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                  <SelectTrigger className="w-[120px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="warm-personal">Warm</SelectItem>
                    <SelectItem value="hot-take">Hot Take</SelectItem>
                  </SelectContent>
                </Select>
                
                {!isRecording && !hasRecording && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={startRecording}
                    disabled={isLoading || isProcessing}
                    className="h-7 px-2 text-xs"
                  >
                    <Mic className="w-3 h-3 mr-1" />
                    Record
                  </Button>
                )}
                
                {isRecording && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={stopRecording}
                      className="h-7 px-2 text-xs"
                    >
                      <MicOff className="w-3 h-3 mr-1" />
                      Stop
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {recordingDuration}s
                    </span>
                  </div>
                )}
                
                {hasRecording && !isRecording && (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={isPlaying ? stopPlaying : playRecording}
                      disabled={isLoading || isProcessing}
                      className="h-7 px-2 text-xs"
                    >
                      {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={processRecording}
                      disabled={isLoading || isProcessing}
                      className="h-7 px-2 text-xs"
                    >
                      <Volume2 className="w-3 h-3 mr-1" />
                      {isProcessing ? 'Processing...' : 'Generate'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={resetVoiceRecording}
                      disabled={isLoading || isProcessing}
                      className="h-7 px-2 text-xs text-muted-foreground"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="Write your review or use voice recording to generate one..."
              className="min-h-[80px]"
              disabled={isProcessing}
            />
            
            {isProcessing && (
              <div className="text-center text-xs text-muted-foreground py-2">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  Processing voice recording...
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSaveEdit}
              disabled={isLoading}
            >
              <Check className="w-3 h-3 mr-1" />
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancelEdit}
              disabled={isLoading}
            >
              <X className="w-3 h-3 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {reviewText ? (
            <div className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
              {reviewText}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No written review.
            </p>
          )}
          
          {isOwnProfile && bookId && (
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                disabled={isLoading}
                className="h-7 px-2 text-xs"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                {reviewText ? 'Edit' : 'Add Review'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRemove}
                disabled={isLoading}
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Remove Book
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ReadRowContent;